import { useCallback, useMemo } from 'react'
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite'

import { Api } from '@lib/api'
import { toSearchParams } from '@utils/object'
import { DataParams, MetadataResponse } from 'types/api'
import { ArrayOrSingle } from 'types/common'

export type InfiniteDataHookOptions<
  Data,
  Params extends DataParams<ArrayOrSingle<Data>> = DataParams<ArrayOrSingle<Data>>
> = {
  params?: Params
  pause?: boolean
  config?: SWRInfiniteConfiguration<Data>
}

export function useDataInfinite<
  D,
  P extends DataParams<ArrayOrSingle<D>> = DataParams<ArrayOrSingle<D>>,
  A extends Api<any> = Api<any>
>(api: A, path: string | null, { params, pause, config }: InfiniteDataHookOptions<D[], P> = {}) {
  const limit = params?.limit ?? 20
  const paramsString = useMemo(() => JSON.stringify(params), [params])

  const getKey = useCallback(
    (pageIndex: number, previousPageData: D[] | null) => {
      if (pause || !path) return null
      if (previousPageData && !previousPageData.length) return null

      const params = paramsString ? JSON.parse(paramsString) : {}
      params.skip = limit * pageIndex
      return [path, params] as [string, P]
    },
    [limit, paramsString, path, pause]
  )

  const _fetcher = useCallback(
    async (currentKey: [string, P]) => {
      const [curPath, curParams] = currentKey

      const response = await api.get<D>(curPath, toSearchParams(curParams as object))
      const metadataCasted = response as MetadataResponse<D[]>
      const straightCasted = response as D[]

      return metadataCasted?.data !== undefined ? metadataCasted.data : straightCasted
    },
    [api]
  )

  const response = useSWRInfinite<D[]>(getKey, _fetcher, config)

  const { data, size, setSize, error, isLoading, isValidating, mutate } = response

  const loadMore = useCallback(() => {
    setSize(size + 1)
  }, [setSize, size])

  const lastPageCount = data?.[data.length - 1]?.length ?? 0

  return {
    data: data?.flat(),
    loadMore,
    hasMore: lastPageCount === limit,
    error,
    isLoading,
    isValidating,
    mutate,
  }
}
